import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-toast>` says about itself.
 *
 * Three capabilities are documented here because the source really has all three, and each gets its
 * own example for that reason and no other: the four intents, the `action` slot, and the opt-in
 * timer. What the source does NOT have is an ambient default duration — a toast with no `duration`
 * set stays until somebody dismisses it, which is stated rather than glossed, because a reader who
 * assumes toasts fade on their own will ship a screen that accumulates them.
 *
 * The `action` slot is a slot and nothing more. The toast draws whatever is put in it beside the
 * message; it does not undo anything and has no notion of what the control means. The source calls
 * the case "an optional undo", so that is what the example shows, but the example says out loud that
 * the doing belongs to the caller.
 *
 * The timer genuinely pauses while the toast is hovered or holds focus (mouseenter/focusin stop it,
 * mouseleave/focusout start it again), so a reader is never raced off a message they are reading.
 * That is in the source; it is not an assumption about how toasts usually behave.
 */
const docs: ComponentDocs = {
  aliases: ['snackbar', 'flash message', 'notification', 'growl', 'transient alert'],

  answers: [
    'tell somebody the save worked',
    'report the outcome of something that already finished',
    'say something failed without stopping what they are doing',
    'let a confirmation get out of the way on its own',
    'offer to undo what just happened',
  ],

  // Declared as NOWHERE. A toast is created in response to an outcome and mounted into a stack; it
  // is never an item somebody drops on a page layout, because a placed one would be a notice
  // announcing something that has not happened.
  placements: [],

  inputs: [
    {
      name: 'intent',
      attribute: 'intent',
      type: 'text',
      options: ['info', 'success', 'warning', 'danger', 'error'],
      description:
        'What kind of outcome this is: info, success, warning or danger. It colours the leading edge ' +
        'and the glyph from the semantic tokens, and danger is also what makes the notice assertive ' +
        'to a screen reader rather than polite. error is accepted as a spelling of danger.',
      default: 'info',
    },
    {
      name: 'duration',
      attribute: 'duration',
      type: 'number',
      description:
        'Milliseconds to stay before dismissing itself. There is NO ambient default: leave it unset ' +
        '(or set it to zero) and no timer ever runs, so the toast stays until it is dismissed. The ' +
        'timer pauses while the toast is hovered or holds focus, and resumes when it is left.',
    },
  ],

  events: [
    {
      name: 'caos-toast-dismiss',
      when:
        'The dismiss control is pressed, the timer elapses, or something calls dismiss(). It is ' +
        'cancelable, and unlike the overlay this piece does act on its own: if nobody prevents the ' +
        'event, the toast removes itself.',
      carries: 'nothing',
    },
  ],

  state: [],

  slots: [
    { name: '', takes: 'The message. One sentence about something that has already happened.' },
    {
      name: 'action',
      takes:
        'An optional control beside the message — an Undo, typically. The toast draws it and nothing ' +
        'more; what pressing it does belongs to whatever raised the toast.',
    },
  ],

  tokens: [
    { token: '--caos-color-surface', controls: 'The body fill, which stays neutral at every intent so the message stays legible.' },
    { token: '--caos-color-border', controls: 'The edge of the notice.' },
    { token: '--caos-color-info', controls: 'The leading accent and glyph at the default intent.' },
    { token: '--caos-color-success', controls: 'The leading accent and glyph at intent success.' },
    { token: '--caos-color-warning', controls: 'The leading accent and glyph at intent warning.' },
    { token: '--caos-color-danger', controls: 'The leading accent and glyph at intent danger.' },
    { token: '--caos-color-text', controls: 'The message text.' },
    { token: '--caos-color-text-muted', controls: 'The resting colour of the dismiss control.' },
    { token: '--caos-shadow-float', controls: 'The one elevation reserved for pieces that float.' },
    { token: '--caos-radius', controls: 'The corner radius of the notice.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of the dismiss control.' },
    { token: '--caos-focus-ring', controls: 'The ring on the dismiss control when it is reached by keyboard.' },
    { token: '--caos-space-3', controls: 'The gap between glyph, message and controls, and the vertical padding.' },
    { token: '--caos-space-4', controls: 'The horizontal padding.' },
    { token: '--caos-font', controls: 'The typeface the message is set in.' },
  ],

  parts: [
    { part: 'toast', is: 'the toast' },
    { part: 'icon', is: 'the intent glyph' },
    { part: 'content', is: 'the message' },
    { part: 'dismiss', is: 'the dismiss control' },
  ],

  methods: [
    {
      name: 'dismiss',
      description:
        'Dismiss it from code — the same path the close control and the timer take, so a host that ' +
        'cancels the event cancels every route to dismissal at once.',
      args: [],
    },
  ],

  placing:
    'A toast is mounted, not placed: something that finished creates one and appends it to a ' +
    'ToastStack, which is what gives it a corner to sit in and a gap between it and the next one. It ' +
    'is a plain block on its own and will render anywhere a block renders, which is what lets a ' +
    'surface that already has a place for it drop one in without a stack at all.',

  whenToUse:
    'The outcome of something that has already happened, needs no answer, and can be missed without ' +
    'harm. Nothing in a toast should be the only place a person could have learned something: it can ' +
    'be dismissed in a keystroke, and if a duration is set it removes itself.',

  insteadUse: [
    {
      name: 'NoticeBanner',
      when:
        'The notice MUST NOT disappear — it needs a decision, or it states a condition of the page ' +
        'that is still true after the person has read it.',
    },
    {
      name: 'ValidationSummary',
      when: 'A save was refused and the person has specific fields to go and fix.',
    },
    {
      name: 'ProgressBar',
      when: 'The thing is still happening rather than finished.',
    },
    {
      name: 'ToastStack',
      when: 'A screen raises several of these and they need somewhere to accumulate and a cap.',
    },
  ],

  accessibility:
    'The notice is a live region on its own: role="status" for info, success and warning, and ' +
    'role="alert" for danger and error, so a failure interrupts and the rest does not. The dismiss ' +
    'control is a real button with an accessible name, and the auto-dismiss timer pauses while the ' +
    'toast is hovered or holds focus so a reader is not raced. What is left to the author: the ' +
    'message has to stand alone when read out of context, and anything a person must act on does not ' +
    'belong in a piece that removes itself.',

  examples: [
    {
      key: 'intents',
      label: 'The four intents',
      summary:
        'Every outcome colour the toast has. The danger one is also the only one announced ' +
        'assertively, which is a difference you cannot see in the picture.',
      placement: [
        { component: 'caos-toast', inputs: { intent: 'info' }, children: ['Totals were refreshed from the account.'] },
        { component: 'caos-toast', inputs: { intent: 'success' }, children: ['Order saved.'] },
        { component: 'caos-toast', inputs: { intent: 'warning' }, children: ['Two line items have no amount.'] },
        { component: 'caos-toast', inputs: { intent: 'danger' }, children: ['The order could not be sent.'] },
      ],
    },
    {
      key: 'action',
      label: 'With an action',
      summary:
        'The action slot holding an Undo beside the message. The toast draws the control; what ' +
        'pressing it does is for the caller to decide, because the toast has no idea what it reported.',
      placement: {
        component: 'caos-toast',
        inputs: { intent: 'success' },
        children: [
          'Line item removed.',
          { component: 'caos-button', inputs: { slot: 'action', variant: 'ghost', size: 'sm' }, children: ['Undo'] },
        ],
      },
    },
    {
      key: 'timed',
      label: 'Dismissing itself',
      summary:
        'The opt-in timer: this one leaves after six seconds, and the timer stops for as long as the ' +
        'pointer is over it or a control inside it holds focus. Reset the example to bring it back.',
      placement: {
        component: 'caos-toast',
        inputs: { intent: 'info', duration: 6000 },
        children: ['Draft saved. This notice leaves on its own.'],
      },
    },
  ],
};

export default docs;
